import { getAccessToken, readStoredUser } from "./authSession";

const DEFAULT_API_ORIGINS = ["https://api.solarent.ir", "http://api.solarent.ir"];

/**
 * Builds browser URL for uploaded files stored under wwwroot/Files.
 * Uses FileUploader/GetFile so the API can serve local files or proxy from FilesPublicBaseUrl.
 * Appends JWT as access_token because <img> and direct links cannot send Authorization headers.
 */
export const getApiOrigin = (pageBaseUrl) => {
  const fromPage = (pageBaseUrl || "").trim().replace(/\/$/, "");
  if (fromPage) return fromPage;
  return String(window.globalConfig?.API_URL || "").trim().replace(/\/$/, "");
};

const getKnownApiOrigins = (pageBaseUrl) => {
  const origins = new Set(DEFAULT_API_ORIGINS.map((o) => o.toLowerCase()));
  const configured = getApiOrigin(pageBaseUrl);
  if (configured) origins.add(configured.toLowerCase());
  return origins;
};

export const normalizeFilesPath = (path) => {
  if (!path) return null;
  let normalized = String(path).trim().replace(/\\/g, "/");
  if (normalized.includes("..")) return null;
  if (
    normalized.startsWith("http://") ||
    normalized.startsWith("https://") ||
    normalized.startsWith("//")
  ) {
    return null;
  }
  if (!normalized.startsWith("/")) normalized = `/${normalized}`;
  if (!normalized.toLowerCase().startsWith("/files/")) {
    normalized = `/Files${normalized === "/" ? "" : normalized}`;
  }
  return normalized;
};

/** Strip API host from stored file URLs so forms keep relative /Files/... paths. */
export const normalizeStoredFilePath = (path, pageBaseUrl = "") => {
  if (!path || typeof path !== "string") return "";
  const trimmed = path.trim();
  if (!trimmed) return "";

  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("//")
  ) {
    try {
      const absolute = trimmed.startsWith("//")
        ? `${window.location.protocol}${trimmed}`
        : trimmed;
      const url = new URL(absolute);
      const origins = getKnownApiOrigins(pageBaseUrl);
      if (origins.has(url.origin.toLowerCase())) {
        const filesPath = normalizeFilesPath(url.pathname);
        if (filesPath) return filesPath;
      }
    } catch {
      return trimmed;
    }
  }

  return normalizeFilesPath(trimmed) || trimmed;
};

const appendAccessToken = (url) => {
  const token = getAccessToken(readStoredUser());
  if (!token) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}access_token=${encodeURIComponent(token)}`;
};

const buildGetFileUrl = (filesPath, pageBaseUrl, includeAuth) => {
  const origin = getApiOrigin(pageBaseUrl);
  if (!origin) return filesPath;
  const url = `${origin}/FileUploader/GetFile?path=${encodeURIComponent(filesPath)}`;
  return includeAuth ? appendAccessToken(url) : url;
};

const tryRewriteAbsoluteApiFilesUrl = (absoluteUrl, pageBaseUrl, includeAuth) => {
  try {
    const url = new URL(
      absoluteUrl.startsWith("//") ? `${window.location.protocol}${absoluteUrl}` : absoluteUrl
    );
    const origins = getKnownApiOrigins(pageBaseUrl);
    if (!origins.has(url.origin.toLowerCase())) return null;

    const filesPath = normalizeFilesPath(url.pathname);
    if (!filesPath) return null;

    return buildGetFileUrl(filesPath, pageBaseUrl, includeAuth);
  } catch {
    return null;
  }
};

export const resolveUploadUrl = (path, baseUrl = "", { includeAuth = true } = {}) => {
  if (!path || typeof path !== "string") return "";

  const trimmed = path.trim();
  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("//")
  ) {
    const rewritten = tryRewriteAbsoluteApiFilesUrl(trimmed, baseUrl, includeAuth);
    return rewritten || trimmed;
  }

  const normalized = normalizeFilesPath(trimmed);
  if (!normalized) return "";

  return buildGetFileUrl(normalized, baseUrl, includeAuth);
};

export const getApiFileBaseUrl = (pageData) => {
  const fromApi = pageData?.baseUrl || pageData?.BaseUrl;
  return getApiOrigin(fromApi);
};
